import { BackstageTicketComponent } from '../components/ticket-template/backstage-ticket-component.js';
import { OpenTicketPass } from '../models/serializable/discounts/open-ticket-pass.js';
import { EventProfile } from '../models/serializable/event-profile.js';
import { EventTicket } from '../models/serializable/event-ticket.js';
import { FlexiblePassProtocol } from '../models/serializable/open-access-pass/flexible-pass-protocol.js';
import { OpenAccessPass } from '../models/serializable/open-access-pass/open-access-pass.js';
import { TicketSupplement } from '../models/serializable/ticket-supplement.js';
import { fromEnumAsString } from '../serialization/enum-utils.js';
import { assertUnreachable } from './general-utility.js';
import { isNotNullOrUndefinedOrEmpty } from './string.js';

export enum QRCodeSeparator {
  Dot = 'Dot',
  Dash = 'Dash',
}

export enum QRCodeType {
  OpenAccessPass = 'OAP',
  OpenTicketPass = 'OTP',
  FlexibleOpenAccessPass = 'OFP',
  EventTicket = 'ETK',
  ThirdPartyEventTicket = 'EXTK',
  TicketSupplement = 'TSP',
}

export interface QRCodeData {
  version: number,
  uniqueId: string,
  type: QRCodeType,
  eventShortCode: string | null,
}

export class QRCodeUtils {
  private static readonly DOT_SEPARATOR = '.';
  private static readonly DASH_SEPARATOR = '-';
  private static readonly LEGACY_STADIUM_PASS_TYPE = 'SP';
  private static readonly SEPARATOR_REGEX: RegExp = /[-.]/;
  private static readonly SHORT_CODE_REGEX: RegExp = /^[A-Z]{4,}$/;
  private static readonly UNIQUE_ID_REGEX: RegExp = /^[a-zA-Z0-9]{20}$/;
  private static readonly OPEN_TICKET_PASS_PREFIX_REGEX: RegExp = /^OP$/;
  private static readonly OPEN_TICKET_PASS_SEGMENT_REGEX: RegExp = /^[A-Z0-9]{5}$/;

  static getQrCodeDataOfTicket(event: EventProfile, ticket: EventTicket): string | null {
    if(event.shortCode == null) {
      return null;
    }

    const qrCodeSegments = [event.shortCode, ticket.uniqueId, 2];
    const separator = this.getQrCodeSeparator({ event, ticket });

    return qrCodeSegments.join(separator);
  }

  static getQrCodeDataOfAccessPass(pass: OpenAccessPass): string | null {
    if(FlexiblePassProtocol.assertType(pass.protocol)) {
      return this._getQrCodeDataOfFlexibleAccessPass(pass);
    }

    return this._getQrCodeDataOfGenericAccessPass(pass);
  }

  private static _getQrCodeDataOfFlexibleAccessPass(pass: OpenAccessPass): string {
    return pass.code;
  }

  private static _getQrCodeDataOfGenericAccessPass(pass: OpenAccessPass): string {
    const qrCodeSegments = [
      fromEnumAsString(QRCodeType.OpenAccessPass),
      pass.code,
      3,
    ];

    const separator = this.getQrCodeSeparator();

    return qrCodeSegments.join(separator);
  }

  static getQrCodeDataOfProduct({ prefix, eventId, orderId, productId, productInstanceId, hashFunction }: {
      prefix: string | null,
      eventId: string,
      orderId: string,
      productId: string,
      productInstanceId: string,
      hashFunction: (x: string) => string,
  }): string {
    const path = [eventId, orderId, productId, productInstanceId].join(QRCodeUtils.DASH_SEPARATOR);
    const hashedPath = hashFunction(path);

    return isNotNullOrUndefinedOrEmpty(prefix) ? `${prefix}${hashedPath}` : hashedPath;
  }

  static getQrCodeDataOfTicketSupplement(
      event: EventProfile,
      ticket: EventTicket,
      supplement: TicketSupplement,
  ): string | null {
    if(event.shortCode == null) {
      return null;
    }

    const qrCodeSegments = [
      fromEnumAsString(QRCodeType.TicketSupplement),
      event.shortCode,
      supplement.ticketId,
      3,
    ];

    const separator = this.getQrCodeSeparator({ event, ticket });
    return qrCodeSegments.join(separator);
  }

  static getQrCodeDataOfOpenTicketPass(pass: OpenTicketPass): string | null {
    const qrCodeSegments = [
      fromEnumAsString(QRCodeType.OpenTicketPass),
      pass.code,
      3,
    ];

    const separator = this.getQrCodeSeparator();
    return qrCodeSegments.join(separator);
  }

  static decodeQRCode(qrCode: string): QRCodeData | null {
    try {
      const components = qrCode.split(this.SEPARATOR_REGEX);
      if(components.length > 1) {
        // HACK: RANDOM FUCKING TICKET WEBSITES PREFIXES THE CODES WITH [RR-]
        if(components[0] == 'RR') {
          return this.decodeGenericQRCode([qrCode]);
        }
        else if(components[0] == QRCodeType.FlexibleOpenAccessPass) {
          return this.decodeQRCode_v3(components);
        }
        else {
          const version = parseInt(components[components.length - 1]);
          if(version === 2) {
            return this.decodeQRCode_v2(components);
          }
          else if(version === 3) {
            return this.decodeQRCode_v3(components);
          }
        }
      }
      else if(components.length > 0) {
        return this.decodeGenericQRCode(components);
      }

      return null;
    }
    catch{
      return null;
    }
  }

  private static getQrCodeSeparator(context: {
    event?: EventProfile | null,
    ticket?: EventTicket | null,
    openTicketPass?: OpenTicketPass | null,
  } = {}): string {
    const component = context.ticket?.template.findComponentByTypeId<BackstageTicketComponent>(
        BackstageTicketComponent.TYPE_ID,
    );

    if(component != null) {
      return QRCodeUtils.DASH_SEPARATOR;
    }

    if(context.event != null) {
      switch(context.event.qrCodeSeparator) {
        case QRCodeSeparator.Dot:
          return QRCodeUtils.DOT_SEPARATOR;
        case QRCodeSeparator.Dash:
          return QRCodeUtils.DASH_SEPARATOR;
        default:
          assertUnreachable(context.event.qrCodeSeparator);
      }
    }

    if(context?.openTicketPass != null) {
      return QRCodeUtils.DASH_SEPARATOR;
    }

    return QRCodeUtils.DOT_SEPARATOR;
  }

  private static decodeGenericQRCode(components: string[]): QRCodeData | null {
    return { version: 3, type: QRCodeType.ThirdPartyEventTicket, uniqueId: components[0], eventShortCode: null };
  }

  private static decodeQRCode_v2(components: string[]): QRCodeData | null {
    if(components.length < 3) return null;

    let match = components[1].match(this.UNIQUE_ID_REGEX);
    if(!match) return null;

    match = components[0].match(this.SHORT_CODE_REGEX);
    if(!match) return null;

    return { version: 2, type: QRCodeType.EventTicket, uniqueId: components[1], eventShortCode: components[0] };
  }

  private static decodeQRCode_v3(components: string[]): QRCodeData | null {
    let eventShortCode: string | null = null;
    let uniqueId: string | null = null;
    let type: QRCodeType | null = null;

    if(components[0] == QRCodeType.OpenAccessPass || components[0] == this.LEGACY_STADIUM_PASS_TYPE) {
      if(components.length < 3) return null;

      const match = components[1].match(this.UNIQUE_ID_REGEX);
      if(match) {
        uniqueId = components[1];
      }

      type = QRCodeType.OpenAccessPass;
    }
    else if(components[0] == QRCodeType.EventTicket) {
      if(components.length < 4) return null;

      let match = components[1].match(this.SHORT_CODE_REGEX);
      if(match) {
        eventShortCode = components[1];
      }

      match = components[2].match(this.UNIQUE_ID_REGEX);
      if(match) {
        uniqueId = components[2];
      }

      type = QRCodeType.EventTicket;
    }
    else if(components[0] == QRCodeType.TicketSupplement) {
      if(components.length < 4) return null;

      let match = components[1].match(this.SHORT_CODE_REGEX);
      if(match) {
        eventShortCode = components[1];
      }

      match = components[2].match(this.UNIQUE_ID_REGEX);
      if(match) {
        uniqueId = components[2];
      }

      type = QRCodeType.TicketSupplement;
    }
    else if(components[0] == QRCodeType.OpenTicketPass) {
      if(components.length < 6) return null;

      let match = components[1].match(this.OPEN_TICKET_PASS_PREFIX_REGEX);
      if(match) {
        uniqueId = components[1];
      }

      match = components[2].match(this.OPEN_TICKET_PASS_SEGMENT_REGEX);
      if(match) {
        uniqueId += '-' + components[2];
      }

      match = components[3].match(this.OPEN_TICKET_PASS_SEGMENT_REGEX);
      if(match) {
        uniqueId += '-' + components[3];
      }

      match = components[4].match(this.OPEN_TICKET_PASS_SEGMENT_REGEX);
      if(match) {
        uniqueId += '-' + components[4];
      }

      type = QRCodeType.OpenTicketPass;
    }
    else if(components[0] == QRCodeType.FlexibleOpenAccessPass) {
      if(components.length !== 4 || components.slice(1).some((x) => !this.OPEN_TICKET_PASS_SEGMENT_REGEX.test(x))) {
        return null;
      }

      uniqueId = components.join(this.DASH_SEPARATOR);
      type = QRCodeType.FlexibleOpenAccessPass;
    }

    if(type == null || uniqueId == null) return null;
    if(type === QRCodeType.EventTicket && eventShortCode == null) return null;
    if(type === QRCodeType.TicketSupplement && eventShortCode == null) return null;
    if(type === QRCodeType.OpenTicketPass && uniqueId.length < 20) return null;
    if(type === QRCodeType.FlexibleOpenAccessPass && uniqueId.length < 20) return null;

    return { version: 3, type, uniqueId, eventShortCode };
  }
}
